import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  IconButton,
  Collapse,
  TextField,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ReactComponent as BlackFilledExpandIcon } from "../../asserts/logo/BlackFilledExpandIcon.svg";
import { ReactComponent as RedXCircleIcon } from "../../asserts/logo/RedXCircle.svg";
import { ReactComponent as GreenCheckCircleIcon } from "../../asserts/logo/GreenCheckCircle.svg";
import { ReactComponent as PlayCircleOutlineIcon } from "../../asserts/logo/PlayCircleOutline.svg";
import { ReactComponent as GovernanceLogo } from "../../asserts/logo/GovernanceLogo.svg";
import { ReactComponent as DatabaseIcon } from "../../asserts/logo/DatabaseIcon.svg";
import { ReactComponent as DatabaseSearchIcon } from "../../asserts/logo/DatabaseSearchIcon.svg";
import { API_BASE_URL } from "../../config";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import IntakeGateTabsBar from "../../components/intake/IntakeGateTabsBar";
import IntakeHeaderBackButton from "../../components/intake/IntakeHeaderBackButton";
import {
  extractSystemReadinessDeclared,
  extractDataReadinessDeclared,
  extractVectorStoreReadinessDeclared,
  extractGovernanceReadinessDeclared,
  verifiedChecksFromReport,
  verifiedDataChecksFromReport,
  verifiedVectorStoreChecksFromReport,
  mergeSystemReadinessChecks,
  mergeDataReadinessChecks,
  scoreCombinedChecks,
  scoreDeclaredChecks,
} from "../../utils/readinessExtractor";
import { fetchReadinessEvidence, fetchReadinessDiscovery, fetchReadinessIntakeSnapshot } from "../../services/readinessClient";
import { gate1SurfacesForDiscovery } from "../../utils/readinessDiscovery";
import {
  loadReadinessCache,
  saveReadinessCache,
} from "../../utils/readinessFromIntake";
import {
  EMPTY_API_FORM,
  EMPTY_CREDENTIALS,
  isApiSurfaceInUse,
  clearApiFromGate0Data,
  mergeApiFormIntoGate0Data,
  applyProbeFieldsToGate0,
} from "../../utils/apiSurfaceForm";
import { saveGate0 } from "../../utils/gate0Storage";
import {
  EMPTY_DB_FORM,
  EMPTY_DB_CREDENTIALS,
  buildDbSaveSummary,
  clearDbFromGate0Data,
  currentDbReport,
  dbFormCanSave,
  dbReportForDeclaredChecks,
  mergeDbFormIntoGate0Data,
  tagDbReportWithForm,
} from "../../utils/dbSurfaceForm";
import {
  connectionReportForDeclaredChecks,
  probeReportForForm,
} from "../../utils/probeDisplayUtils";
import {
  preserveSavedConnectionDropdowns,
  resolveApiFormForReadiness,
  resolveDbFormForReadiness,
} from "../../utils/readinessPrefillFromIntake";
import {
  applyMonitoringSignalsUrl,
  applyReadinessLinkUrls,
  buildDeploymentPlanSave,
  buildMonitoringSignalsSave,
  deploymentPlanHref,
  resolveDeploymentPlanUrl,
  resolveMonitoringSignalsUrl,
  validateApiBaseUrl,
  validateDeploymentPlanUrl,
  validateMonitoringSignalsUrl,
} from "../../utils/validateApiBaseUrl";
import {
  requiredFieldErrorsForReadiness,
} from "../../utils/readinessSectionRequired";
import Gate1ApiSurfacePanel from "../../components/readiness/Gate1ApiSurfacePanel";
import Gate1DbSurfacePanel from "../../components/readiness/Gate1DbSurfacePanel";
import Gate1VectorStorePanel from "../../components/readiness/Gate1VectorStorePanel";
import {
  EMPTY_VECTOR_STORE_FORM,
  EMPTY_VECTOR_STORE_CREDENTIALS,
  buildVectorStoreSaveSummary,
  clearVectorStoreFromGate0Data,
  mergeVectorFormIntoGate0Data,
  mergeVectorProviderSelectionIntoGate0Data,
  vectorCredentialsFromGate0Data,
  vectorStoreFormCanSave,
  vectorStoreFormFromDiscovery,
  vectorStoreFormFromGate0Data,
  postgresReachabilityFromVectorReport,
  tagVectorReportWithForm,
  vectorReportMatchesForm,
} from "../../utils/vectorStoreSurfaceForm";
import { clearTraceFromGate0Data } from "../../utils/traceSurfaceForm";
import { useAuth } from "../../context/AuthContext";
import { canOnboard } from "../../utils/entitlements";
import { postGate0FeaturesEnabled } from "../../config/productFeatures";
import { GATE1_CONNECTOR_ELEMENT_ID } from "../gate2/gate1ConnectorNav";

const F = INTAKE_FONT;

/** Fluid page padding — grows with viewport so content fills large monitors. */
const PAGE_PX = { xs: 1.5, sm: 2, md: 3, lg: 4, xl: 6 };

/**
 * Back-arrow placement — same 24px / 16px ink standard as Gate 0 App Identity,
 * chatbot, summary, and Gate 1 Guided Assurance. CSS backs out the 6px
 * hit-target inset and Lato's ~1px capital side bearing. Other header gaps stay 8px.
 */
const BACK_ARROW_INK_LEFT_PX = 24;
const BACK_ARROW_INK_GAP_RIGHT_PX = 16;
const BACK_ARROW_HIT_TARGET_INSET_PX = 6;
const BACK_ARROW_BUTTON_SIZE_PX = 32;
const TITLE_LEFT_SIDE_BEARING_PX = 1;
const HEADER_INSET_LEFT_PX = BACK_ARROW_INK_LEFT_PX - BACK_ARROW_HIT_TARGET_INSET_PX;
const BACK_ARROW_GAP_RIGHT_PX =
  BACK_ARROW_INK_GAP_RIGHT_PX - BACK_ARROW_HIT_TARGET_INSET_PX - TITLE_LEFT_SIDE_BEARING_PX;
const HEADER_ROW_GAP_PX = 8;
const TITLE_NUDGE_LEFT_PX = BACK_ARROW_GAP_RIGHT_PX - HEADER_ROW_GAP_PX;
const HEADER_TEXT_COLUMN_INSET_PX = BACK_ARROW_BUTTON_SIZE_PX + BACK_ARROW_GAP_RIGHT_PX;

function StatusIcon({ status }) {
  if (status === "present" || status === "passed") {
    return <GreenCheckCircleIcon style={{ width: 15, height: 15, display: "block" }} />;
  }
  if (status === "missing" || status === "failed") {
    return <RedXCircleIcon style={{ width: 15, height: 15, display: "block" }} />;
  }
  if (status === "warning") {
    return <WarningAmberIcon sx={{ fontSize: 15, color: INTAKE_COLORS.amber }} />;
  }
  return <PlayCircleOutlineIcon style={{ width: 15, height: 15, display: "block" }} />;
}

function scoreColor(score) {
  if (score >= 85) return INTAKE_COLORS.green;
  if (score >= 70) return INTAKE_COLORS.amber;
  return INTAKE_COLORS.red;
}

function isReportSuccess(status) {
  const s = String(status || "").toLowerCase();
  return (
    s === "ready" ||
    s === "ready_with_warnings" ||
    s === "pass" ||
    s === "pass_with_warnings"
  );
}

function statusMeta(status) {
  if (status === "present" || status === "passed") {
    return { label: status === "passed" ? "Verified" : "Declared", bucket: "declared" };
  }
  if (status === "missing" || status === "failed") {
    return { label: status === "failed" ? "Failed" : "Missing", bucket: "missing" };
  }
  if (status === "warning") {
    return { label: "Warning", bucket: "missing" };
  }
  if (status === "unavailable") {
    return { label: "Unavailable", bucket: "unavailable" };
  }
  return { label: "Not run", bucket: "notRun" };
}

function CheckRow({ check }) {
  const [open, setOpen] = useState(false);
  const meta = statusMeta(check.status);
  const detail = String(check.value || "").trim();
  const hasDetail = detail.length > 0;
  const detailLines = detail.split(/\r?\n/);

  const statusColor =
    meta.bucket === "declared"
      ? "#00754E"
      : meta.bucket === "missing"
        ? "#B82500"
        : "#374151";

  return (
    <Box sx={{ borderBottom: "1px solid #F3F4F6" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          py: "10px",
          minHeight: 39,
          boxSizing: "border-box",
        }}
      >
        <IconButton
          size="small"
          disabled={!hasDetail}
          onClick={() => hasDetail && setOpen((v) => !v)}
          aria-label={open ? "Hide description" : "Show description"}
          aria-expanded={open}
          sx={{
            width: 15,
            height: 15,
            minWidth: 15,
            p: 0,
            color: "#383838",
            flexShrink: 0,
            "&.Mui-disabled": { color: "#383838" },
          }}
        >
          <BlackFilledExpandIcon
            style={{
              width: 15,
              height: 15,
              display: "block",
              transform: open ? "rotate(90deg)" : "none",
            }}
          />
        </IconButton>

        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          <StatusIcon status={check.status} />
        </Box>

        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "100%",
            letterSpacing: 0,
            color: "#383838",
            fontFamily: F,
            wordBreak: "break-word",
          }}
        >
          {check.label}
        </Typography>

        <Box
          sx={{
            flexShrink: 0,
            px: "8px",
            py: "3px",
            borderRadius: "100px",
            bgcolor:
              meta.bucket === "declared"
                ? "#E8FCF3"
                : meta.bucket === "missing"
                  ? "#FFD2C7"
                  : "#F3F4F6",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: statusColor,
              fontFamily: F,
              lineHeight: "100%",
              letterSpacing: 0,
              whiteSpace: "nowrap",
            }}
          >
            {meta.label}
          </Typography>
        </Box>
      </Box>

      <Collapse in={open && hasDetail} unmountOnExit>
        <Box
          sx={{
            bgcolor: "#F5F5F5",
            px: "12px",
            py: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {detailLines.map((line, index) => {
              const trimmedLine = line.trim();
              const previousLine = detailLines[index - 1]?.trim() || "";
              const isHeading =
                index === 0 ||
                (!trimmedLine.startsWith("•") &&
                  !trimmedLine.startsWith("-") &&
                  !trimmedLine.endsWith(":") &&
                  index > 0 &&
                  (previousLine === "" ||
                    previousLine.startsWith("•") ||
                    previousLine.startsWith("-")));

              return (
                <Typography
                  key={`${check.id}-detail-${index}`}
                  sx={{
                    m: 0,
                    minHeight: trimmedLine ? "auto" : "4px",
                    fontSize: isHeading ? "13px" : "12px",
                    fontWeight: isHeading ? 600 : 400,
                    color: isHeading ? "#383838" : "#555555",
                    fontFamily: F,
                    lineHeight: "100%",
                    letterSpacing: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {line || " "}
                </Typography>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

function LegendDot({ color, label, count }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
      <Typography
        component="span"
        sx={{
          fontSize: "12px",
          lineHeight: "100%",
          fontWeight: 700,
          color,
          fontFamily: F,
          whiteSpace: "nowrap",
        }}
      >
        {count} {label}
      </Typography>
    </Box>
  );
}

function DomainCard({
  title,
  score,
  checks,
  subtitle,
  disabled,
  scoreTextColor,
  progressColor,
  HeaderIcon,
  headerIconWidth = 24,
  headerIconHeight = 24,
}) {
  const counts = (checks || []).reduce(
    (acc, c) => {
      const bucket = statusMeta(c.status).bucket;
      acc[bucket] += 1;
      return acc;
    },
    { declared: 0, missing: 0, notRun: 0, unavailable: 0 }
  );

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "6px",
        bgcolor: disabled ? INTAKE_COLORS.g1 : "#fff",
        opacity: disabled ? 0.72 : 1,
        overflow: "hidden",
        height: { xs: "auto", md: 639 },
        minHeight: { xs: 0, md: 639 },
        minWidth: 0,
        width: "100%",
        p: "16px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", flexShrink: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: HeaderIcon ? "36px minmax(0, 1fr) auto" : "minmax(0, 1fr) auto",
            gridTemplateRows: "auto auto",
            alignItems: "flex-start",
            columnGap: "16px",
            rowGap: "8px",
            minHeight: 51,
          }}
        >
          {HeaderIcon && (
            <Box
              sx={{
                gridRow: "1 / 3",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HeaderIcon
                style={{
                  width: headerIconWidth,
                  height: headerIconHeight,
                  display: "block",
                }}
              />
            </Box>
          )}
          <Typography
            sx={{
              minWidth: 0,
              fontSize: "24px",
              fontWeight: 700,
              lineHeight: "100%",
              letterSpacing: 0,
              color: "#383838",
              fontFamily: F,
            }}
          >
            {title}
          </Typography>
          {!disabled && (
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                color: scoreTextColor || scoreColor(score),
                lineHeight: "100%",
                letterSpacing: 0,
                fontFamily: F,
                flexShrink: 0,
              }}
            >
              {score}%
            </Typography>
          )}
          {subtitle && (
            <Typography
              sx={{
                gridColumn: HeaderIcon ? "2 / -1" : "1 / -1",
                minWidth: 0,
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "100%",
                letterSpacing: 0,
                color: "#6C7A82",
                fontFamily: F,
                whiteSpace: "nowrap",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {!disabled && (
          <Box
            sx={{
              width: "100%",
              height: 4,
              bgcolor: "#F5F5F5",
              borderRadius: "100px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${Math.max(0, Math.min(100, score))}%`,
                height: "100%",
                bgcolor: progressColor || scoreColor(score),
              }}
            />
          </Box>
        )}
        {!disabled && (
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
            <LegendDot color="#00754E" label="Declared" count={counts.declared} />
            <Typography sx={{ fontSize: "12px", lineHeight: "100%", color: "#D1D5DB", fontFamily: F }}>•</Typography>
            <LegendDot color="#DE350B" label="Missing" count={counts.missing} />
            <Typography sx={{ fontSize: "12px", lineHeight: "100%", color: "#D1D5DB", fontFamily: F }}>•</Typography>
            <LegendDot color="#6B7080" label="Not run" count={counts.notRun} />
          </Box>
        )}
      </Box>
      {checks?.length > 0 && (
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            overflowY: "auto",
            borderTop: "1px solid #E5E7EB",
            scrollbarWidth: "thin",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            pt: "16px",
          }}
        >
          {checks.map((c) => (
            <CheckRow key={c.id} check={c} />
          ))}
        </Box>
      )}
    </Card>
  );
}

export default function Gate1ReadinessPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const connectorSurface = String(searchParams.get("surface") || "").trim();
  const { user } = useAuth();
  const readOnly = !canOnboard(user?.roles) || !postGate0FeaturesEnabled();

  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState("");
  const [progress, setProgress] = useState(null);
  const [projectGate0, setProjectGate0] = useState({});
  const [apiForm, setApiForm] = useState(() => ({ ...EMPTY_API_FORM, endpoints: [...EMPTY_API_FORM.endpoints] }));
  const [savingDeclaration, setSavingDeclaration] = useState(false);
  const [declarationSaved, setDeclarationSaved] = useState(false);
  const [connectionReport, setConnectionReport] = useState(null);
  const [dbReport, setDbReport] = useState(null);
  const [credentials, setCredentials] = useState({ ...EMPTY_CREDENTIALS });
  const [dbForm, setDbForm] = useState({ ...EMPTY_DB_FORM });
  const [dbCredentials, setDbCredentials] = useState({ ...EMPTY_DB_CREDENTIALS });
  const [vectorForm, setVectorForm] = useState({ ...EMPTY_VECTOR_STORE_FORM });
  const [vectorCredentials, setVectorCredentials] = useState({ ...EMPTY_VECTOR_STORE_CREDENTIALS });
  const [vectorReport, setVectorReport] = useState(null);
  const [staleVectorReport, setStaleVectorReport] = useState(null);
  const [savingVector, setSavingVector] = useState(false);
  const [vectorSaved, setVectorSaved] = useState(false);
  const [savingDb, setSavingDb] = useState(false);
  const [dbSaved, setDbSaved] = useState(false);
  const [removingApi, setRemovingApi] = useState(false);
  const [removingDb, setRemovingDb] = useState(false);
  const [removingVector, setRemovingVector] = useState(false);
  const [toast, setToast] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");
  const [evidenceByLabel, setEvidenceByLabel] = useState({});
  const [resolverUnavailable, setResolverUnavailable] = useState(false);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);
  const [baseUrlGateError, setBaseUrlGateError] = useState("");
  const [monitoringSignalsUrl, setMonitoringSignalsUrl] = useState("");
  const [monitoringUrlError, setMonitoringUrlError] = useState("");
  const [savingMonitoring, setSavingMonitoring] = useState(false);
  const [deploymentPlanUrl, setDeploymentPlanUrl] = useState("");
  const [deploymentPlanUrlError, setDeploymentPlanUrlError] = useState("");
  const [savingDeploymentPlan, setSavingDeploymentPlan] = useState(false);
  const [discovery, setDiscovery] = useState(null);
  const baseUrlInputRef = useRef(null);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    // Reset session form/secret state on project change, then restore persisted
    // Gate 1 connector secrets (vector_store) from gate0_data below.
    setCredentials({ ...EMPTY_CREDENTIALS });
    setDbCredentials({ ...EMPTY_DB_CREDENTIALS });
    setVectorCredentials({ ...EMPTY_VECTOR_STORE_CREDENTIALS });
    setVectorForm({ ...EMPTY_VECTOR_STORE_FORM });
    setVectorReport(null);
    setStaleVectorReport(null);
    const cached = loadReadinessCache(projectId);
    // Do not restore API / DB / vector secrets from session cache.
    if (cached?.connectionReport) {
      setConnectionReport(cached.connectionReport);
    }
    if (cached?.dbReport) {
      setDbReport(cached.dbReport);
    }
    if (cached?.vectorReport) {
      setVectorReport(cached.vectorReport);
    }
    if (cached?.staleVectorReport) {
      setStaleVectorReport(cached.staleVectorReport);
    }

    setLoading(true);
    const evidencePromise = fetchReadinessEvidence(projectId);
    const discoveryPromise = fetchReadinessDiscovery(projectId);

    (async () => {
      try {
        const projectRes = await axios.get(`${API_BASE_URL}/projects/${projectId}`).catch(() => null);
        if (cancelled) return;
        const projectRow = projectRes?.data?.data;
        setProjectName(projectRow?.project_name || "");
        const fromProject = projectRow?.gate0_data || {};
        setProjectGate0(fromProject);
        if (fromProject.gate1_readiness_summary && !cached?.connectionReport) {
          setConnectionReport(fromProject.gate1_readiness_summary);
        }

        try {
          const snap = await fetchReadinessIntakeSnapshot(projectId);
          if (cancelled) return;
          const hasSession = Boolean(snap?.success && snap.data?.has_session);
          const outcomeCaptured = !hasSession
            ? (
              projectRow?.intake_outcome?.captured ||
              projectRow?.intake_outcome?.understanding?.captured ||
              {}
            )
            : {};
          const captured = hasSession ? (snap.data.captured || {}) : outcomeCaptured;
          const sessionGate0 = hasSession ? (snap.data.gate0_data || {}) : {};
          setProgress({ captured, gate0_data: sessionGate0 });
          const gate0 = preserveSavedConnectionDropdowns(fromProject, sessionGate0);
          setApiForm(resolveApiFormForReadiness(gate0, captured));
          setDbForm({
            ...resolveDbFormForReadiness(gate0, captured),
            username: "",
          });
          setVectorForm(vectorStoreFormFromGate0Data(gate0));
          setVectorCredentials(vectorCredentialsFromGate0Data(hasSession ? gate0 : fromProject));
          setMonitoringSignalsUrl(resolveMonitoringSignalsUrl(fromProject, sessionGate0));
          setDeploymentPlanUrl(resolveDeploymentPlanUrl(fromProject, sessionGate0));
          const reportSource = hasSession ? gate0 : fromProject;
          if (reportSource.gate1_data_readiness_summary) {
            setDbReport(reportSource.gate1_data_readiness_summary);
          }
          if (reportSource.gate1_vector_store_readiness_summary && !cached?.vectorReport) {
            setVectorReport(reportSource.gate1_vector_store_readiness_summary);
          }
          if (!hasSession) {
            setToast("No active intake session — API declaration can still be saved to this project.");
            setToastSeverity("warning");
          }
        } catch (err) {
          if (!cancelled) {
            setMonitoringSignalsUrl(resolveMonitoringSignalsUrl(fromProject));
            setDeploymentPlanUrl(resolveDeploymentPlanUrl(fromProject));
            setToast(err.response?.data?.error || err.message || "Failed to load readiness data.");
            setToastSeverity("error");
          }
        }

        const [ev, disc] = await Promise.all([evidencePromise, discoveryPromise]);
        if (cancelled) return;
        if (!ev?.cancelled) {
          const results = ev?.data?.results;
          setEvidenceByLabel(results && typeof results === "object" ? results : {});
          setResolverUnavailable(Boolean(ev?.unavailable) || ev?.success === false);
        }
        setDiscovery(disc?.data || null);
        if (disc?.data) {
          setVectorForm((prev) => vectorStoreFormFromDiscovery(disc.data, prev));
        }
      } catch (err) {
        if (cancelled) return;
        setEvidenceByLabel({});
        setResolverUnavailable(true);
        setDiscovery(null);
        setToast(err.response?.data?.error || err.message || "Failed to load readiness data.");
        setToastSeverity("error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (loading) return;
    if (!vectorReport) return;
    if (vectorReportMatchesForm(vectorReport, vectorForm)) return;
    setStaleVectorReport(vectorReport);
    setVectorReport(null);
    if (projectId) {
      saveReadinessCache(projectId, {
        vectorReport: null,
        staleVectorReport: vectorReport,
      });
    }
  }, [loading, vectorForm, vectorReport, projectId]);

  const gate0Data = useMemo(() => {
    const base = clearTraceFromGate0Data({ ...projectGate0, ...(progress?.gate0_data || {}) });
    const withApi = mergeApiFormIntoGate0Data(base, apiForm, credentials);
    const withDb = mergeDbFormIntoGate0Data(withApi, dbForm);
    const withVector = mergeVectorProviderSelectionIntoGate0Data(withDb, vectorForm);
    return applyReadinessLinkUrls(withVector, {
      monitoringUrl: monitoringSignalsUrl,
      deploymentPlanUrl,
    });
  }, [projectGate0, progress?.gate0_data, apiForm, dbForm, credentials, vectorForm, monitoringSignalsUrl, deploymentPlanUrl]);

  const captured = progress?.captured || {};
  const surfaces = useMemo(() => gate1SurfacesForDiscovery(discovery), [discovery]);

  useEffect(() => {
    if (loading) return;
    const elementId = GATE1_CONNECTOR_ELEMENT_ID[connectorSurface];
    if (!elementId) return;
    const timer = window.setTimeout(() => {
      document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loading, connectorSurface, surfaces.showApiSurface, surfaces.showDbSurface, surfaces.showVectorStoreSurface]);
  const semantic = useMemo(
    () => ({ evidenceByLabel, resolverUnavailable }),
    [evidenceByLabel, resolverUnavailable]
  );

  const activeConnectionReport = useMemo(
    () => probeReportForForm(connectionReport, apiForm),
    [connectionReport, apiForm]
  );

  const declaredChecks = useMemo(
    () =>
      extractSystemReadinessDeclared(
        gate0Data,
        captured,
        semantic,
        connectionReportForDeclaredChecks(
          connectionReport,
          gate0Data?.gate1_readiness_summary || gate0Data?.api_probe_summary,
          apiForm
        )
      ),
    [gate0Data, captured, semantic, connectionReport, apiForm]
  );

  const verifiedChecks = useMemo(
    () => verifiedChecksFromReport(activeConnectionReport),
    [activeConnectionReport]
  );

  const systemChecks = useMemo(
    () => mergeSystemReadinessChecks(declaredChecks, verifiedChecks),
    [declaredChecks, verifiedChecks]
  );

  const systemScore = useMemo(() => scoreCombinedChecks(systemChecks), [systemChecks]);

  const activeDbReport = currentDbReport(dbReport, dbForm);

  const dataDeclaredChecks = useMemo(
    () => [
      ...extractDataReadinessDeclared(
        gate0Data,
        captured,
        semantic,
        dbReportForDeclaredChecks(dbReport, gate0Data?.gate1_data_readiness_summary, dbForm)
      ),
      ...extractVectorStoreReadinessDeclared(vectorReport, {
        currentProvider: vectorForm.provider,
      }),
    ],
    [gate0Data, captured, semantic, dbReport, dbForm, vectorReport, vectorForm.provider]
  );

  const dataVerifiedChecks = useMemo(
    () => [
      ...verifiedDataChecksFromReport(activeDbReport, {
        postgresReachability:
          postgresReachabilityFromVectorReport(vectorReport) ||
          postgresReachabilityFromVectorReport(staleVectorReport),
      }),
      ...verifiedVectorStoreChecksFromReport(vectorReport, {
        previousReport: staleVectorReport,
        currentProvider: vectorForm.provider,
      }),
    ],
    [activeDbReport, vectorReport, staleVectorReport, vectorForm.provider]
  );

  const dataChecks = useMemo(
    () => mergeDataReadinessChecks(dataDeclaredChecks, dataVerifiedChecks),
    [dataDeclaredChecks, dataVerifiedChecks]
  );

  const dataScore = useMemo(() => scoreCombinedChecks(dataChecks), [dataChecks]);

  const governanceChecks = useMemo(
    () => extractGovernanceReadinessDeclared(gate0Data, captured, semantic),
    [gate0Data, captured, semantic]
  );

  const governanceScore = useMemo(() => scoreDeclaredChecks(governanceChecks), [governanceChecks]);

  const saveReadinessDeclaration = async () => {
    let merged = mergeDbFormIntoGate0Data(
      mergeApiFormIntoGate0Data(
        { ...projectGate0, ...(progress?.gate0_data || {}) },
        apiForm,
        credentials
      ),
      dbForm
    );
    merged = mergeVectorProviderSelectionIntoGate0Data(clearTraceFromGate0Data(merged), vectorForm);
    merged = applyReadinessLinkUrls(merged, {
      monitoringUrl: monitoringSignalsUrl,
      deploymentPlanUrl,
    });
    if (vectorStoreFormCanSave(vectorForm, vectorCredentials)) {
      merged = mergeVectorFormIntoGate0Data(merged, vectorForm, vectorCredentials);
    }
    try {
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: merged,
      });
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Failed to save readiness declaration.");
      }
      setProjectGate0(merged);
      setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
      saveGate0(projectId, merged);
      setDeclarationSaved(true);
      setToast("Readiness declaration saved.");
      setToastSeverity("success");
      return merged;
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to save readiness declaration.";
      setToast(msg);
      setToastSeverity("error");
      return null;
    } finally {
      setSavingDeclaration(false);
    }
  };

  const persistClearedGate0 = async (next) => {
    const stamped = applyReadinessLinkUrls(next, {
      monitoringUrl: monitoringSignalsUrl,
      deploymentPlanUrl,
    });
    const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
      gate0_data: stamped,
    });
    if (!res.data?.success) {
      throw new Error(res.data?.error || "Failed to remove connection details.");
    }
    setProjectGate0(stamped);
    setProgress((prev) => (prev ? { ...prev, gate0_data: stamped } : prev));
    saveGate0(projectId, stamped);
    return stamped;
  };

  const saveApiDeclaration = async () => {
    if (isApiSurfaceInUse(apiForm)) {
      const baseCheck = validateApiBaseUrl(apiForm.baseUrl);
      if (baseCheck.value !== String(apiForm.baseUrl ?? "")) {
        setApiForm((prev) => ({ ...prev, baseUrl: baseCheck.value }));
      }
      const urlError = String(apiForm.baseUrl || "").trim()
        ? baseCheck.ok
          ? ""
          : baseCheck.error
        : "API base URL is required";
      if (urlError) {
        setBaseUrlGateError(urlError);
        setShowRequiredErrors(true);
        setToast(urlError);
        setToastSeverity("error");
        requestAnimationFrame(() => baseUrlInputRef.current?.focus());
        return null;
      }
    }
    setBaseUrlGateError("");
    setSavingDeclaration(true);
    return saveReadinessDeclaration();
  };

  const handleConnectionReport = (report) => {
    setConnectionReport(report);
    saveReadinessCache(projectId, {
      credentials,
      dbCredentials,
      connectionReport: report,
      dbReport,
      vectorReport,
      staleVectorReport,
    });

    const dataForCheck = applyProbeFieldsToGate0(gate0Data, report);
    const summary = {
      overall_status: report.overall_status,
      generated_at: report.generated_at,
      probe_mode: report.probe_mode,
      gate0_can_proceed: report.gate0_can_proceed,
      endpoints: (report.endpoints || []).map((ep) => ({
        endpoint_id: ep.endpoint_id,
        method: ep.method,
        path: ep.path,
        url: ep.url,
        reachable: ep.reachable,
        auth_status: ep.auth_status,
        schema_status: ep.schema_status,
        status_code: ep.status_code,
        response_body_preview: ep.response_body_preview,
        config_fingerprint: ep.config_fingerprint,
      })),
    };
    const mergedGate0 = { ...dataForCheck, gate1_readiness_summary: summary };
    setProjectGate0(mergedGate0);
    setProgress((prev) => (prev ? { ...prev, gate0_data: mergedGate0 } : prev));
    axios
      .put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: mergedGate0,
      })
      .catch(() => {});

    const ok = isReportSuccess(report.overall_status);
    const hint =
      !ok && Array.isArray(report.recommendations) && report.recommendations[0]
        ? ` ${report.recommendations[0]}`
        : !ok && Array.isArray(report.flags_summary) && report.flags_summary[0]?.message
          ? ` ${report.flags_summary[0].message}`
          : "";
    setToast(
      ok ? "System checks completed successfully." : `System checks completed — issues found.${hint}`
    );
    setToastSeverity(ok ? "success" : "warning");
  };

  const handleDbReport = (report) => {
    const tagged = tagDbReportWithForm(report, dbForm);
    setDbReport(tagged);
    saveReadinessCache(projectId, {
      credentials,
      dbCredentials,
      connectionReport,
      dbReport: tagged,
      vectorReport,
      staleVectorReport,
    });

    const summary = {
      overall_status: tagged.overall_status,
      generated_at: tagged.generated_at,
      probe_mode: tagged.probe_mode,
      connected: tagged.connected,
      db_type: tagged.db_type,
      host: tagged.host,
      port: tagged.port,
      database: tagged.database,
      username: tagged.username,
      latency_ms: tagged.latency_ms,
      table_check: tagged.table_check || null,
      config_fingerprint: tagged.config_fingerprint,
    };
    axios
      .put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: mergeDbFormIntoGate0Data(
          { ...gate0Data, gate1_data_readiness_summary: summary },
          dbForm
        ),
      })
      .catch(() => {});

    const ok = String(tagged.overall_status || "").toLowerCase() === "ready";
    setToast(ok ? "Database check completed successfully." : "Database check completed — issues found.");
    setToastSeverity(ok ? "success" : "warning");
    if (ok) setDbSaved(true);
  };

  const handleVectorReport = (report) => {
    const tagged = tagVectorReportWithForm(report, vectorForm);
    setVectorReport(tagged);
    setStaleVectorReport(null);
    saveReadinessCache(projectId, {
      credentials,
      dbCredentials,
      connectionReport,
      dbReport,
      vectorReport: tagged,
      staleVectorReport: null,
    });

    const summary = {
      overall_status: tagged.overall_status,
      generated_at: tagged.generated_at,
      probe_mode: tagged.probe_mode,
      access_method: tagged.access_method || "vector-store",
      operation_type: tagged.operation_type || "similarity_search",
      status: tagged.status,
      provider: tagged.provider,
      success: tagged.success,
      connection: tagged.connection,
      operation: tagged.operation,
      response: tagged.response || null,
      latencyMs: tagged.latencyMs,
      host: tagged.host,
      port: tagged.port,
      database: tagged.database,
      table: tagged.table,
      vectorColumn: tagged.vectorColumn,
      dimension: tagged.dimension,
      indexName: tagged.indexName || tagged.index_name || null,
      namespace: tagged.namespace || null,
      recordId: tagged.recordId || tagged.record?.id || null,
      // Remaining non-secret connection fields, so Gate 2 execution can reuse
      // this verified connection. Secrets are stored separately on vector_store.
      username: tagged.username || null,
      collection: tagged.collection || null,
      vectorPath: tagged.vectorPath || tagged.vector_path || null,
      numCandidates: tagged.numCandidates ?? null,
      topK: tagged.response?.top_k ?? null,
      record: tagged.record
        ? {
            id: tagged.record.id,
            metadata: tagged.record.metadata || {},
            vector_dimension: Array.isArray(tagged.record.values)
              ? tagged.record.values.length
              : tagged.record.vector_dimension ?? null,
          }
        : null,
      warning: tagged.warning || null,
      config_fingerprint: tagged.config_fingerprint,
    };
    const merged = mergeVectorFormIntoGate0Data(
      { ...gate0Data, gate1_vector_store_readiness_summary: summary },
      vectorForm,
      vectorCredentials
    );
    axios
      .put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: merged,
      })
      .then((res) => {
        if (res.data?.success) {
          setProjectGate0((prev) => ({ ...prev, ...merged }));
          setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
          saveGate0(projectId, merged);
        }
      })
      .catch(() => {});

    const ok = report?.success === true || String(report?.overall_status || "").toLowerCase() === "ready";
    setToast(
      ok ? "Vector store check completed successfully." : "Vector store check completed — issues found."
    );
    setToastSeverity(ok ? "success" : "warning");
    if (ok) setVectorSaved(true);
  };

  const persistOptionalSummaries = async (gate0) => {
    const next = { ...gate0 };
    if (dbFormCanSave(dbForm)) {
      const summary = buildDbSaveSummary(dbForm, currentDbReport(dbReport, dbForm));
      Object.assign(next, mergeDbFormIntoGate0Data(next, dbForm));
      next.gate1_data_readiness_summary = summary;
      setDbReport(summary);
      setDbSaved(true);
    }
    if (vectorStoreFormCanSave(vectorForm, vectorCredentials)) {
      const summary = buildVectorStoreSaveSummary(vectorForm, vectorReport);
      Object.assign(next, mergeVectorFormIntoGate0Data(next, vectorForm, vectorCredentials));
      next.gate1_vector_store_readiness_summary = summary;
      setVectorReport(summary);
      setVectorSaved(true);
    }
    return applyReadinessLinkUrls(clearTraceFromGate0Data(next), {
      monitoringUrl: monitoringSignalsUrl,
      deploymentPlanUrl,
    });
  };

  const handleDbSave = async () => {
    if (!dbFormCanSave(dbForm)) return;
    setSavingDb(true);
    try {
      const summary = buildDbSaveSummary(dbForm, currentDbReport(dbReport, dbForm));
      const merged = mergeDbFormIntoGate0Data(
        { ...gate0Data, gate1_data_readiness_summary: summary },
        dbForm
      );
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: merged,
      });
      if (!res.data?.success) throw new Error(res.data?.error || "Failed to save the database connection.");
      setDbReport(summary);
      setDbSaved(true);
      setProjectGate0((prev) => ({ ...prev, ...merged }));
      setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
      saveGate0(projectId, merged);
      saveReadinessCache(projectId, { dbReport: summary });
      setToast("Database connection details saved.");
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to save the database connection.");
      setToastSeverity("error");
    } finally {
      setSavingDb(false);
    }
  };

  const handleVectorSave = async () => {
    if (!vectorStoreFormCanSave(vectorForm, vectorCredentials)) return;
    setSavingVector(true);
    try {
      const summary = buildVectorStoreSaveSummary(vectorForm, vectorReport);
      const merged = mergeVectorFormIntoGate0Data(
        { ...gate0Data, gate1_vector_store_readiness_summary: summary },
        vectorForm,
        vectorCredentials
      );
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: merged,
      });
      if (!res.data?.success) throw new Error(res.data?.error || "Failed to save the vector connection.");
      setVectorReport(summary);
      setVectorSaved(true);
      setProjectGate0((prev) => ({ ...prev, ...merged }));
      setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
      saveGate0(projectId, merged);
      saveReadinessCache(projectId, { vectorReport: summary });
      setToast("Vector store connection saved for test execution.");
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to save the vector connection.");
      setToastSeverity("error");
    } finally {
      setSavingVector(false);
    }
  };

  const handleApiRemoveDetails = async () => {
    setRemovingApi(true);
    try {
      const next = clearApiFromGate0Data({ ...projectGate0, ...(progress?.gate0_data || {}) });
      await persistClearedGate0(next);
      setApiForm({ ...EMPTY_API_FORM, endpoints: JSON.parse(JSON.stringify(EMPTY_API_FORM.endpoints)) });
      setCredentials({ ...EMPTY_CREDENTIALS });
      setConnectionReport(null);
      setDeclarationSaved(false);
      saveReadinessCache(projectId, { connectionReport: null });
      setToast("API connection details removed.");
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to remove API details.");
      setToastSeverity("error");
    } finally {
      setRemovingApi(false);
    }
  };

  const handleDbRemoveDetails = async () => {
    setRemovingDb(true);
    try {
      const next = clearDbFromGate0Data({ ...projectGate0, ...(progress?.gate0_data || {}) });
      await persistClearedGate0(next);
      setDbForm({ ...EMPTY_DB_FORM });
      setDbCredentials({ ...EMPTY_DB_CREDENTIALS });
      setDbReport(null);
      setDbSaved(false);
      saveReadinessCache(projectId, { dbReport: null });
      setToast("Database connection details removed.");
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to remove database details.");
      setToastSeverity("error");
    } finally {
      setRemovingDb(false);
    }
  };

  const handleVectorRemoveDetails = async () => {
    setRemovingVector(true);
    try {
      const next = clearVectorStoreFromGate0Data({ ...projectGate0, ...(progress?.gate0_data || {}) });
      await persistClearedGate0(next);
      setVectorForm({ ...EMPTY_VECTOR_STORE_FORM });
      setVectorCredentials({ ...EMPTY_VECTOR_STORE_CREDENTIALS });
      setVectorReport(null);
      setStaleVectorReport(null);
      setVectorSaved(false);
      saveReadinessCache(projectId, { vectorReport: null, staleVectorReport: null });
      setToast("Vector store connection details removed.");
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to remove vector store details.");
      setToastSeverity("error");
    } finally {
      setRemovingVector(false);
    }
  };

  /* Visible asterisks (API base URL and auth, once that section is started)
   * keep Gate 1 disabled until they are filled. */
  const requiredFieldErrors = useMemo(
    () =>
      requiredFieldErrorsForReadiness({
        apiForm,
        dbForm,
        dbCredentials,
        vectorForm,
        vectorCredentials,
      }),
    [apiForm, dbForm, dbCredentials, vectorForm, vectorCredentials]
  );

  const gate1BlockedByRequired = !readOnly && Object.keys(requiredFieldErrors.api || {}).length > 0;

  const apiErrorsForForm = (() => {
    if (!showRequiredErrors && !baseUrlGateError) return null;
    const api = showRequiredErrors ? { ...requiredFieldErrors.api } : {};
    if (baseUrlGateError) api.baseUrl = baseUrlGateError;
    return Object.keys(api).length > 0 ? api : null;
  })();

  const handleMonitoringSave = async () => {
    const outcome = buildMonitoringSignalsSave(gate0Data, monitoringSignalsUrl);
    if (!outcome.ok) {
      setMonitoringUrlError(outcome.message);
      setToast(outcome.message);
      setToastSeverity("error");
      return;
    }
    setMonitoringUrlError("");
    if (outcome.value !== monitoringSignalsUrl) setMonitoringSignalsUrl(outcome.value);
    if (readOnly || !projectId) return;
    setSavingMonitoring(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: outcome.gate0,
      });
      if (!res.data?.success) throw new Error(res.data?.error || "Failed to save the monitoring URL.");
      setProjectGate0(outcome.gate0);
      setProgress((prev) => (prev ? { ...prev, gate0_data: outcome.gate0 } : prev));
      saveGate0(projectId, outcome.gate0);
      setToast(outcome.message);
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to save the monitoring URL.");
      setToastSeverity("error");
    } finally {
      setSavingMonitoring(false);
    }
  };

  const handleMonitoringBlur = () => {
    const check = validateMonitoringSignalsUrl(monitoringSignalsUrl);
    if (check.value !== monitoringSignalsUrl) setMonitoringSignalsUrl(check.value);
    setMonitoringUrlError(check.ok ? "" : check.error);
    if (!check.ok || readOnly || !projectId) return;
    const merged = applyMonitoringSignalsUrl(gate0Data, check.value);
    axios
      .put(`${API_BASE_URL}/projects/${projectId}`, { gate0_data: merged })
      .then((res) => {
        if (!res.data?.success) return;
        setProjectGate0(merged);
        setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
        saveGate0(projectId, merged);
      })
      .catch(() => {});
  };

  const handleDeploymentPlanSave = async () => {
    const outcome = buildDeploymentPlanSave(gate0Data, deploymentPlanUrl);
    if (!outcome.ok) {
      setDeploymentPlanUrlError(outcome.message);
      setToast(outcome.message);
      setToastSeverity("error");
      return;
    }
    setDeploymentPlanUrlError("");
    if (outcome.value !== deploymentPlanUrl) setDeploymentPlanUrl(outcome.value);
    if (readOnly || !projectId) return;
    setSavingDeploymentPlan(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
        gate0_data: outcome.gate0,
      });
      if (!res.data?.success) throw new Error(res.data?.error || "Failed to save the deployment plan link.");
      setProjectGate0(outcome.gate0);
      setProgress((prev) => (prev ? { ...prev, gate0_data: outcome.gate0 } : prev));
      saveGate0(projectId, outcome.gate0);
      setToast(outcome.message);
      setToastSeverity("success");
    } catch (err) {
      setToast(err.response?.data?.error || err.message || "Failed to save the deployment plan link.");
      setToastSeverity("error");
    } finally {
      setSavingDeploymentPlan(false);
    }
  };

  const handleDeploymentPlanBlur = () => {
    const check = validateDeploymentPlanUrl(deploymentPlanUrl);
    if (check.value !== deploymentPlanUrl) setDeploymentPlanUrl(check.value);
    setDeploymentPlanUrlError(check.ok ? "" : check.error);
    if (!check.ok || readOnly || !projectId) return;
    const merged = applyReadinessLinkUrls(gate0Data, {
      monitoringUrl: monitoringSignalsUrl,
      deploymentPlanUrl: check.value,
    });
    axios
      .put(`${API_BASE_URL}/projects/${projectId}`, { gate0_data: merged })
      .then((res) => {
        if (!res.data?.success) return;
        setProjectGate0(merged);
        setProgress((prev) => (prev ? { ...prev, gate0_data: merged } : prev));
        saveGate0(projectId, merged);
      })
      .catch(() => {});
  };

  const handleNext = async () => {
    if (gate1BlockedByRequired) return;
    const monitoringCheck = validateMonitoringSignalsUrl(monitoringSignalsUrl);
    const planCheck = validateDeploymentPlanUrl(deploymentPlanUrl);
    setMonitoringUrlError(monitoringCheck.ok ? "" : monitoringCheck.error);
    setDeploymentPlanUrlError(planCheck.ok ? "" : planCheck.error);
    if (!monitoringCheck.ok || !planCheck.ok) return;
    setBaseUrlGateError("");
    if (!readOnly) {
      const merged = await persistOptionalSummaries(gate0Data);
      try {
        const res = await axios.put(`${API_BASE_URL}/projects/${projectId}`, {
          gate0_data: merged,
        });
        if (res.data?.success) setProjectGate0(merged);
      } catch {
        /* Leave the page even if the save fails; the form is unchanged. */
      }
    }
    navigate(`/gate1/${projectId}`);
  };

  const deploymentPlanLink = deploymentPlanHref(deploymentPlanUrl);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        height: "100%",
        minHeight: "100%",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        fontFamily: F,
        boxSizing: "border-box",
      }}
    >
      <IntakeGateTabsBar
        activeGate={1}
        unlockedUpTo={1}
        onGateClick={(gateIndex) => {
          if (gateIndex === 0) navigate(`/chat/${projectId}`);
          if (gateIndex === 1) return;
        }}
        showBack={false}
        showViewSummary={false}
      />

      <Box
        sx={{
          flexShrink: 0,
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        {/* Figma Frame 1410120102 — heading + description card */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "100%",
            boxSizing: "border-box",
            pt: "24px",
            pr: "24px",
            pb: "24px",
            pl: `${HEADER_INSET_LEFT_PX}px`,
            bgcolor: "#FFFFFF",
            border: "1px solid #E6E6E6",
            opacity: 1,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: `${HEADER_ROW_GAP_PX}px`,
              width: "100%",
              minWidth: 0,
            }}
          >
            <IntakeHeaderBackButton onClick={() => navigate(`/summary/${projectId}`)} />
            <Typography
              sx={{
                ml: `${TITLE_NUDGE_LEFT_PX}px`,
                flex: "1 1 auto",
                minWidth: 0,
                minHeight: 29,
                m: 0,
                fontFamily: "Lato, sans-serif",
                fontWeight: 900,
                fontSize: "24px",
                lineHeight: "100%",
                letterSpacing: 0,
                color: "#383838",
              }}
            >
              Gate 1 — System &amp; Evaluation Readiness
            </Typography>
          </Box>
          <Typography
            sx={{
              width: "100%",
              minHeight: 19,
              m: 0,
              pl: `${HEADER_TEXT_COLUMN_INSET_PX}px`,
              boxSizing: "border-box",
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: 0,
              color: "#555555",
            }}
          >
            System readiness checks live APIs with apiAgent. Data readiness includes a basic PostgreSQL
            or MySQL check. Vector store is optional — fill it in only if you want that check now.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: PAGE_PX,
          pt: { xs: 2, md: 2.5 },
          pb: { xs: 2, md: 3 },
          boxSizing: "border-box",
          width: "100%",
        }}
      >
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            {surfaces.showApiSurface ? (
            <Box id={GATE1_CONNECTOR_ELEMENT_ID.api} sx={{ scrollMarginTop: 24 }}>
            <Gate1ApiSurfacePanel
              projectId={projectId}
              appName={projectName}
              apiForm={apiForm}
              onApiFormChange={
                readOnly
                  ? undefined
                  : (next) => {
                      setApiForm(next);
                      setDeclarationSaved(false);
                      setBaseUrlGateError("");
                    }
              }
              credentials={credentials}
              onCredentialsChange={readOnly ? undefined : setCredentials}
              onSaveDeclaration={readOnly ? undefined : saveApiDeclaration}
              savingDeclaration={savingDeclaration}
              declarationSaved={declarationSaved}
              onRemoveDetails={readOnly ? undefined : handleApiRemoveDetails}
              removingDetails={removingApi}
              connectionReport={connectionReport}
              onConnectionReport={readOnly ? undefined : handleConnectionReport}
              readOnly={readOnly}
              errors={apiErrorsForForm}
              baseUrlInputRef={baseUrlInputRef}
            />
            </Box>
            ) : null}

            {surfaces.showDbSurface ? (
            <Gate1DbSurfacePanel
              projectId={projectId}
              dbForm={dbForm}
              onDbFormChange={
                readOnly
                  ? undefined
                  : (next) => {
                      setDbForm(next);
                      setDbSaved(false);
                    }
              }
              dbCredentials={dbCredentials}
              onDbCredentialsChange={readOnly ? undefined : setDbCredentials}
              dbReport={dbReport}
              onDbReport={readOnly ? undefined : handleDbReport}
              onSaveConnection={readOnly ? undefined : handleDbSave}
              onRemoveDetails={readOnly ? undefined : handleDbRemoveDetails}
              saving={savingDb}
              removing={removingDb}
              saved={dbSaved}
              readOnly={readOnly}
            />
            ) : null}

            {surfaces.showVectorStoreSurface ? (
            <Box id={GATE1_CONNECTOR_ELEMENT_ID.vector} sx={{ scrollMarginTop: 24 }}>
            <Gate1VectorStorePanel
              projectId={projectId}
              vectorForm={vectorForm}
              onVectorFormChange={
                readOnly
                  ? undefined
                  : (next) => {
                      setVectorForm(next);
                      setVectorSaved(false);
                    }
              }
              vectorCredentials={vectorCredentials}
              onVectorCredentialsChange={readOnly ? undefined : setVectorCredentials}
              vectorReport={vectorReport}
              staleVectorReport={staleVectorReport}
              onVectorReport={readOnly ? undefined : handleVectorReport}
              onSaveConnection={readOnly ? undefined : handleVectorSave}
              onRemoveDetails={readOnly ? undefined : handleVectorRemoveDetails}
              saving={savingVector}
              removing={removingVector}
              saved={vectorSaved}
              readOnly={readOnly}
            />
            </Box>
            ) : null}

            <Box
              sx={{
                boxSizing: "border-box",
                width: "100%",
                p: "24px",
                borderRadius: "8px",
                border: "1px solid #E6E6E6",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography
                sx={{
                  m: 0,
                  mb: "16px",
                  fontFamily: F,
                  fontWeight: 900,
                  fontSize: "20px",
                  lineHeight: "100%",
                  color: "#383838",
                }}
              >
                Monitoring Signals
              </Typography>
              <Typography
                component="label"
                htmlFor="gate1-monitoring-signals-url"
                sx={{
                  display: "block",
                  m: 0,
                  mb: "8px",
                  fontFamily: F,
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#707070",
                }}
              >
                Monitoring URL
              </Typography>
              <TextField
                id="gate1-monitoring-signals-url"
                size="small"
                fullWidth
                placeholder="https://monitoring.example.com"
                value={monitoringSignalsUrl}
                disabled={readOnly}
                error={Boolean(monitoringUrlError)}
                helperText={monitoringUrlError || ""}
                onChange={(e) => {
                  setMonitoringSignalsUrl(e.target.value);
                  if (monitoringUrlError) setMonitoringUrlError("");
                }}
                onBlur={handleMonitoringBlur}
                inputProps={{ autoComplete: "off", name: "monitoring-signals-url" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: "4px",
                    bgcolor: "#FFFFFF",
                    fontFamily: F,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#000000",
                    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
                    "& fieldset": { borderColor: "#707070" },
                    "&:hover fieldset": { borderColor: "#707070" },
                    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 10px",
                      "&::placeholder": { color: "#707070", opacity: 1 },
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    mx: 0,
                    mt: "4px",
                    textAlign: "right",
                    fontFamily: F,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: "#DE350B",
                  },
                }}
              />
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="outlined"
                  disableElevation
                  disabled={savingMonitoring}
                  onClick={handleMonitoringSave}
                  sx={{
                    mt: "12px",
                    width: "fit-content",
                    minWidth: 88,
                    height: 32,
                    p: "4px 16px",
                    textTransform: "none",
                    fontFamily: F,
                    fontWeight: 700,
                    fontSize: "13px",
                    lineHeight: "24px",
                    borderRadius: "4px",
                    border: `1px solid ${INTAKE_COLORS.blue}`,
                    color: INTAKE_COLORS.blue,
                    bgcolor: "#FFFFFF",
                    "&:hover": {
                      border: `1px solid ${INTAKE_COLORS.blue}`,
                      bgcolor: INTAKE_COLORS.primary20,
                    },
                    "&.Mui-disabled": {
                      border: "1px solid #E6E6E6",
                      bgcolor: "#F5F5F5",
                      color: "#BBBBBB",
                    },
                  }}
                >
                  {savingMonitoring ? "Saving…" : "Save"}
                </Button>
              )}
            </Box>

            <Box
              sx={{
                boxSizing: "border-box",
                width: "100%",
                p: "24px",
                borderRadius: "8px",
                border: "1px solid #E6E6E6",
                bgcolor: "#FFFFFF",
              }}
            >
              <Typography
                sx={{
                  m: 0,
                  mb: "16px",
                  fontFamily: F,
                  fontWeight: 900,
                  fontSize: "20px",
                  lineHeight: "100%",
                  color: "#383838",
                }}
              >
                Deployment Plan
              </Typography>
              <Typography
                component="label"
                htmlFor="gate1-deployment-plan-url"
                sx={{
                  display: "block",
                  m: 0,
                  mb: "8px",
                  fontFamily: F,
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "21px",
                  color: "#707070",
                }}
              >
                Deployment plan URL
              </Typography>
              <TextField
                id="gate1-deployment-plan-url"
                size="small"
                fullWidth
                placeholder="https://confluence.example.com/deployment-plan"
                value={deploymentPlanUrl}
                disabled={readOnly}
                error={Boolean(deploymentPlanUrlError)}
                helperText={deploymentPlanUrlError || ""}
                onChange={(e) => {
                  setDeploymentPlanUrl(e.target.value);
                  if (deploymentPlanUrlError) setDeploymentPlanUrlError("");
                }}
                onBlur={handleDeploymentPlanBlur}
                inputProps={{ autoComplete: "off", name: "deployment-plan-url" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: "4px",
                    bgcolor: "#FFFFFF",
                    fontFamily: F,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#000000",
                    boxShadow: "inset 0px 2px 3px 0px rgba(85, 85, 85, 0.2)",
                    "& fieldset": { borderColor: "#707070" },
                    "&:hover fieldset": { borderColor: "#707070" },
                    "&.Mui-focused fieldset": { borderColor: "#707070", borderWidth: "1px" },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 10px",
                      "&::placeholder": { color: "#707070", opacity: 1 },
                    },
                  },
                  "& .MuiFormHelperText-root": {
                    mx: 0,
                    mt: "4px",
                    textAlign: "right",
                    fontFamily: F,
                    fontSize: "12px",
                    lineHeight: "18px",
                    color: "#DE350B",
                  },
                }}
              />
              {deploymentPlanLink ? (
                <Box
                  component="a"
                  href={deploymentPlanLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "inline-block",
                    mt: "8px",
                    fontFamily: F,
                    fontWeight: 700,
                    fontSize: "14px",
                    lineHeight: "21px",
                    color: INTAKE_COLORS.blue,
                    textDecoration: "underline",
                    wordBreak: "break-all",
                  }}
                >
                  {deploymentPlanLink}
                </Box>
              ) : null}
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="outlined"
                  disableElevation
                  disabled={savingDeploymentPlan}
                  onClick={handleDeploymentPlanSave}
                  sx={{
                    mt: "12px",
                    display: "block",
                    width: "fit-content",
                    minWidth: 88,
                    height: 32,
                    p: "4px 16px",
                    textTransform: "none",
                    fontFamily: F,
                    fontWeight: 700,
                    fontSize: "13px",
                    lineHeight: "24px",
                    borderRadius: "4px",
                    border: `1px solid ${INTAKE_COLORS.blue}`,
                    color: INTAKE_COLORS.blue,
                    bgcolor: "#FFFFFF",
                    "&:hover": {
                      border: `1px solid ${INTAKE_COLORS.blue}`,
                      bgcolor: INTAKE_COLORS.primary20,
                    },
                    "&.Mui-disabled": {
                      border: "1px solid #E6E6E6",
                      bgcolor: "#F5F5F5",
                      color: "#BBBBBB",
                    },
                  }}
                >
                  {savingDeploymentPlan ? "Saving…" : "Save"}
                </Button>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
                gap: { xs: 1.5, md: 2 },
                width: "100%",
                minWidth: 0,
              }}
            >
              <DomainCard
                title="System Readiness"
                score={systemScore.score}
                checks={systemChecks}
                subtitle="API declarations + live connectivity check (apiAgent)."
                HeaderIcon={DatabaseSearchIcon}
              />
              <DomainCard
                title="Data Readiness"
                score={dataScore.score}
                checks={dataChecks}
                subtitle="Intake descriptions + live SQL DB. Vector store optional."
                HeaderIcon={DatabaseIcon}
              />
              <DomainCard
                title="Governance"
                score={governanceScore.score}
                checks={governanceChecks}
                subtitle="Intake — ownership, controls, compliance."
                HeaderIcon={GovernanceLogo}
                headerIconWidth={21}
                headerIconHeight={26}
              />
            </Box>
          </Stack>
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          height: 56,
          px: PAGE_PX,
          py: 0,
          bgcolor: "#fff",
          width: "100%",
          boxSizing: "border-box",
          boxShadow: "0px -1px 8px 0px #2122260A",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 1.5, sm: 2 },
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(`/summary/${projectId}`)}
            sx={{
              textTransform: "none",
              fontFamily: F,
              fontWeight: 600,
              fontSize: { xs: 14, sm: 15 },
              height: 40,
              minWidth: { xs: 72, sm: 88 },
              px: 2,
              borderRadius: "4px",
              bgcolor: "#fff",
              color: INTAKE_COLORS.blue,
              border: `1px solid ${INTAKE_COLORS.blue}`,
              "&:hover": {
                bgcolor: INTAKE_COLORS.primary20,
                border: `1px solid ${INTAKE_COLORS.blue}`,
              },
            }}
          >
            Back
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              disableElevation
              disabled={gate1BlockedByRequired}
              onClick={handleNext}
              sx={{
                textTransform: "none",
                fontFamily: F,
                fontWeight: 600,
                fontSize: { xs: 14, sm: 15 },
                height: 40,
                minWidth: { xs: 72, sm: 88 },
                px: 2.5,
                borderRadius: "4px",
                bgcolor: INTAKE_COLORS.blue,
                color: "#fff",
                "&:hover": { bgcolor: INTAKE_COLORS.blueDark },
                "&.Mui-disabled": {
                  bgcolor: "#E6E6E6",
                  color: "#A0A0A0",
                },
              }}
            >
              Gate 1
            </Button>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(toast)}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast("")} sx={{ fontFamily: F }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
